import axios, { AxiosError, AxiosHeaders } from "axios";
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { getSession } from "next-auth/react";
import { getToken } from "@/context/NonAuthProvider";

export const AXIOS_INSTANCE = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BACKEND_URL,
});

const PUBLIC_URLS = [
  "all-info",
  "dates-available",
  "terms-and-conditions",
  "start-end-date",
  "details",
  "detail-info",
  "hours-available",
  "comments",
  "location",
  "tariffs",
];
const PROTECTED_GROUP = "reservation";

AXIOS_INSTANCE.interceptors.request.use(async (request) => {
  const lastPath =
    request.url?.split("/")[request.url?.split("/").length - 1] ?? "";
  const endpointGroup = request.url?.split("/")[3] ?? "";
  //this is for avoid multiple calls to session endpoint
  if (PUBLIC_URLS.includes(lastPath) && endpointGroup != PROTECTED_GROUP) {
    return {
      ...request,
      headers: new AxiosHeaders({
        ...request.headers,
        "app-token": process.env.NEXT_PUBLIC_APP_TOKEN!,
      }),
    } as InternalAxiosRequestConfig;
  }
  const session = await getSession();
  if (session) {
    return {
      ...request,
      headers: new AxiosHeaders({
        ...request.headers,
        Authorization: `Bearer ${session.user.token}`,
        "app-token": process.env.NEXT_PUBLIC_APP_TOKEN!,
      }),
    } as InternalAxiosRequestConfig;
  }
  if (getToken() != "") {
    return {
      ...request,
      headers: new AxiosHeaders({
        ...request.headers,
        "X-AnonAuthorization": getToken(),
        "app-token": process.env.NEXT_PUBLIC_APP_TOKEN!,
      }),
    } as InternalAxiosRequestConfig;
  }

  return {
    ...request,
    headers: new AxiosHeaders({
      ...request.headers,
      "app-token": process.env.NEXT_PUBLIC_APP_TOKEN!,
    }),
  } as InternalAxiosRequestConfig;
});

export const instance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source();
  /* Temporal fix to bypass content type issue on get methods */
  const isGet = !config?.method || config.method === "get";
  const body = isGet ? {} : config.data;
  config.data = body;
  /* -------------------------------------------------------- */
  const promise = AXIOS_INSTANCE({ ...config, cancelToken: source.token }).then(
    ({ data }) => data,
  );

  // @ts-ignore
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
};

export type ErrorType<Error> = AxiosError<Error>;
